/**
 * Cache Manager - 缓存管理
 *
 * 负责管理缓存有效性检查、缓存清理和缓存时间戳更新
 */

import type { FavoritesCenterState, UserData } from "../types";

export class CacheManager {
  constructor(
    private sourceData: Map<string, UserData>,
    private state: FavoritesCenterState,
    private getCurrentUserId: () => string | null,
  ) {}

  /**
   * Get uncached content IDs
   */
  getUncachedContentIds(contentIds: string[]): string[] {
    return contentIds.filter(
      (id) => this.state.favoriteStates.data[id] === undefined,
    );
  }

  /**
   * Check if user folders cache is valid
   * @param cacheTimeout Cache timeout in milliseconds
   * @returns Whether cache is valid
   */
  isUserFoldersCacheValid(cacheTimeout: number = 5 * 60 * 1000): boolean {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return false;
    }

    const userData = this.sourceData.get(currentUserId);
    if (
      !userData ||
      !userData.userFolders ||
      userData.userFolders.length === 0
    ) {
      return false;
    }

    const now = Date.now();
    return now - userData.lastAccess < cacheTimeout;
  }

  /**
   * Clear user folders cache
   */
  clearUserFoldersCache(): void {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return;
    }

    const userData = this.sourceData.get(currentUserId);
    if (userData) {
      userData.userFolders = undefined;
      userData.lastAccess = 0;
    }

    // Clear active data
    this.state.userFolders = undefined;
  }

  /**
   * Update cache timestamp
   */
  updateCacheTimestamp(): void {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return;
    }

    const userData = this.sourceData.get(currentUserId);
    if (userData) {
      userData.lastAccess = Date.now();
    }
  }

  /**
   * Get cached user folders
   */
  getCachedUserFolders(): any[] | undefined {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return undefined;
    }

    const userData = this.sourceData.get(currentUserId);
    return userData?.userFolders;
  }
}
